import { useEffect, useRef, useState } from 'react';
import { Player } from '@lottiefiles/react-lottie-player';
import environment from '@config/environment';
import TokenManager from '@viome/viome-base/src/api/TokenManager';
import AgentChatLottie from '@/assets/agent_chat.json';

interface PeacockChatProps {
  email: string;
}

const CHAT_BASE_URL = `${environment.PEACOCK_EXT_URL}/peacock-chat.html`;
// Pinning targetOrigin to the iframe's known origin prevents the auth token
// from leaking to a navigated-away origin in the iframe.
const IFRAME_ORIGIN = new URL(environment.PEACOCK_EXT_URL).origin;

export const PeacockChat = ({ email }: PeacockChatProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Token now arrives via postMessage handshake after the iframe mounts —
  // no URL exposure. See useEffect below.
  const src = `${CHAT_BASE_URL}?d_email=${encodeURIComponent(email)}`;

  // Reply to the iframe's auth handshake while the chat is open. The iframe
  // posts {type:'peacock:auth-request'} once mounted; we reply with the
  // current TokenManager value, pinned to IFRAME_ORIGIN.
  useEffect(() => {
    if (!isOpen) return;

    function handleMessage(event: MessageEvent) {
      if (event.origin !== IFRAME_ORIGIN) return;
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (event.data?.type !== 'peacock:auth-request') return;

      const token = TokenManager.getToken();
      iframeRef.current?.contentWindow?.postMessage(
        { type: 'peacock:auth-response', token },
        IFRAME_ORIGIN,
      );
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isOpen]);

  if (!isOpen) {
    return (
      <button
        type="button"
        aria-label="Open Viome AI Health Assistant"
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 flex h-16 w-16 items-center justify-center rounded-full drop-shadow-lg transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      >
        <Player
          autoplay
          loop
          src={AgentChatLottie}
          className="h-full w-full"
        />
      </button>
    );
  }

  return (
    <div
      role="dialog"
      aria-label="Viome AI Health Assistant"
      className="fixed inset-0 z-50 flex flex-col bg-white shadow-xl sm:inset-auto sm:bottom-6 sm:right-6 sm:h-[600px] sm:w-[400px] sm:max-h-[calc(100vh-3rem)] sm:rounded-xl sm:border sm:border-border"
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <span className="text-sm font-semibold">
          Viome AI Health Assistant
        </span>
        <button
          type="button"
          aria-label="Close chat"
          onClick={() => setIsOpen(false)}
          className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-5 w-5"
            aria-hidden="true"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </div>
      <iframe
        ref={iframeRef}
        src={src}
        title="Viome AI Health Assistant"
        className="flex-1 w-full border-0"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
        allow="clipboard-write"
      />
    </div>
  );
};
